import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Bidorah <SeoHead> — drop this at the TOP of every page component.
 *
 * Provides per-route control over:
 *  - <title>            → tab + Google blue-link title
 *  - meta description   → Google snippet
 *  - canonical          → de-dups paginated / filter-variant URLs
 *  - Open Graph (FB, IG, LinkedIn, iMessage rich preview)
 *  - Twitter card
 *  - robots             → set noindex on private routes (cart, settings, etc.)
 *  - JSON-LD structured data (Organization, Product, BroadcastEvent, etc.)
 *
 * Defaults match the canonical brand copy on index.html so anything you DON'T
 * override stays brand-consistent. Pass `jsonLd` as either an object or an
 * array to render multiple schema.org blocks on the same page.
 */
export default function SeoHead({
  title,
  description = "Bidorah — Live auctions & verified marketplace. Sneakers, trading cards, jewelry, fragrances & more. Every seller US-verified, wholesale strictly separated from retail. Bid live, win real.",
  canonical,
  image = "https://bidorah.com/icon-512.png",
  type = "website",
  noindex = false,
  keywords,
  jsonLd,
  children,
}) {
  // Build the final page title: keep the tab brand suffix unless caller has
  // already included it. Google truncates around ~60 chars so we keep the
  // brand at the END for non-home pages so the unique part wins.
  const fullTitle = !title
    ? "Bidorah — Live Auctions, Verified Sellers"
    : /bidorah/i.test(title)
      ? title
      : `${title} · Bidorah`;
  const ogUrl =
    canonical ||
    (typeof window !== "undefined" ? window.location.href : "https://bidorah.com/");
  const lds = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={ogUrl} />
      {/* Robots */}
      <meta
        name="robots"
        content={
          noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
        }
      />
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Bidorah" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {/* Structured data */}
      {lds.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
      {children}
    </Helmet>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   JSON-LD helpers — every page can compose these without re-typing the
   schema.org plumbing. All output is canonical bidorah.com URLs so
   Google groups every site signal under the same domain.
   ──────────────────────────────────────────────────────────────────── */

export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Bidorah",
  alternateName: "Bidorah Live Auctions",
  url: "https://bidorah.com",
  logo: "https://bidorah.com/icon-512.png",
  sameAs: [
    "https://play.google.com/store/apps/details?id=com.bidorah.app",
    // Add Twitter/X, Instagram, TikTok, etc. once live
  ],
  description:
    "Verified live-auction marketplace for collectors. Strict US photo-ID verification, wholesale and retail strictly separated.",
});

export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Bidorah",
  url: "https://bidorah.com",
  // Sitelinks search box — lets Google show a search field directly under
  // the brand result.
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://bidorah.com/browse?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const productLd = (listing) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: listing.title,
  description: listing.description || listing.title,
  image: listing.images || [],
  sku: listing.id,
  category: listing.category,
  brand: { "@type": "Brand", name: listing.brand || "Bidorah" },
  offers: {
    "@type": "Offer",
    url: `https://bidorah.com/listing/${listing.id}`,
    priceCurrency: "USD",
    price: Number(listing.price || listing.current_bid || 0).toFixed(2),
    availability:
      listing.status === "active"
        ? "https://schema.org/InStock"
        : "https://schema.org/SoldOut",
    seller: { "@type": "Organization", name: "Bidorah" },
  },
});

export const liveStreamLd = (stream) => ({
  "@context": "https://schema.org",
  "@type": "BroadcastEvent",
  name: stream.title || `${stream.seller_name} live on Bidorah`,
  description: stream.description || "Live auction stream on Bidorah",
  isLiveBroadcast: stream.status === "live",
  startDate: stream.started_at || stream.created_at,
  endDate: stream.ended_at || undefined,
  url: `https://bidorah.com/stream/${stream.id}`,
  publishedOn: {
    "@type": "BroadcastService",
    name: "Bidorah",
    url: "https://bidorah.com",
  },
});

export const itemListLd = (items, name = "Bidorah listings") => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.slice(0, 30).map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `https://bidorah.com/listing/${it.id}`,
    name: it.title,
  })),
});

export const faqLd = (qa) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map((p) => ({
    "@type": "Question",
    name: p.q,
    acceptedAnswer: { "@type": "Answer", text: p.a },
  })),
});

export const mobileAppLd = () => ({
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "Bidorah",
  operatingSystem: "ANDROID",
  applicationCategory: "ShoppingApplication",
  downloadUrl:
    "https://play.google.com/store/apps/details?id=com.bidorah.app",
  installUrl:
    "https://play.google.com/store/apps/details?id=com.bidorah.app",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
});
